import { DateTime } from "luxon";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

const DeliveryHoursSettings = forwardRef(({ customer, onSave }, ref) => {
  const [range, setRange] = useState([8, 22]);
  const [timezone, setTimezone] = useState("Europe/Paris");
  const [currentTime, setCurrentTime] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTimezones, setFilteredTimezones] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (customer?.deliveryHours) {
      setRange([
        customer.deliveryHours.from.hour,
        customer.deliveryHours.to.hour,
      ]);
      setTimezone(customer.deliveryHours.timezone);
    }
  }, [customer]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowDropdown(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const now = DateTime.now().setZone(timezone);
      setCurrentTime(now.toFormat("HH:mm"));
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [timezone]);

  useEffect(() => {
    const allTimezones = Intl.supportedValuesOf("timeZone");
    if (searchTerm === "") {
      setFilteredTimezones(allTimezones.slice(0, 10));
    } else {
      const filtered = allTimezones.filter((tz) =>
        tz.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredTimezones(filtered.slice(0, 10));
    }
  }, [searchTerm]);

  const getTimezoneLabel = (tz) => {
    const now = DateTime.now().setZone(tz);
    const offset = now.toFormat("ZZ");
    return `${tz} (UTC${offset})`;
  };

  const getParisEquivalent = () => {
    if (timezone === "Europe/Paris") return null;

    const now = DateTime.now();
    const selectedTzOffset = now.setZone(timezone).offset;
    const parisOffset = now.setZone("Europe/Paris").offset;
    const diffMinutes = parisOffset - selectedTzOffset;
    const diffHours = diffMinutes / 60;

    let parisStart = range[0] + diffHours;
    let parisEnd = range[1] + diffHours;

    const normalizedStart = ((parisStart % 24) + 24) % 24;
    const normalizedEnd = ((parisEnd % 24) + 24) % 24;

    const segments = [];

    if (parisStart < 0) {
      segments.push([parisStart + 24, 24]);
      segments.push([0, parisEnd]);
    } else if (parisEnd > 24) {
      segments.push([parisStart, 24]);
      segments.push([0, parisEnd - 24]);
    } else {
      segments.push([parisStart, parisEnd]);
    }

    return { 
      segments, 
      start: normalizedStart, 
      end: normalizedEnd,
      rawStart: parisStart,
      rawEnd: parisEnd
    };
  };

  const getParisEquivalentForDisplay = () => {
    const equiv = getParisEquivalent();
    if (!equiv) return null;
    return equiv;
  };

  const getSliderMarks = () => {
    const marks = {};
    for (let i = 0; i <= 24; i += 1) {
      marks[i] = `${i}h`;
    }
    return marks;
  };

  const formatTimeRange = (start, end, tz) => {
    const now = DateTime.now().setZone(tz);
    const startTime = now.set({ hour: Math.floor(start), minute: Math.round((start % 1) * 60) });
    const endTime = now.set({ hour: Math.floor(end), minute: Math.round((end % 1) * 60) });
    return {
      start: startTime.toFormat("HH:mm"),
      end: endTime.toFormat("HH:mm")
    };
  };

  const isNightRisk = () => {
    return range[0] < 8 || range[1] > 22;
  };

  const handleSave = async () => {
    await onSave({
      deliveryHours: {
        from: { hour: range[0], minute: 0 },
        to: { hour: range[1], minute: 0 },
        timezone: timezone,
      },
    });
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  return (
    <div className="p-4 border rounded-lg bg-white shadow-sm">
      <div className="mb-4 text-sm text-gray-600">
        Current time in {timezone}: <strong>{currentTime}</strong>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Timezone
        </label>
        <input
          type="text"
          placeholder="Search timezone..."
          value={showDropdown ? searchTerm : getTimezoneLabel(timezone)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          className="w-full p-2 border rounded mb-2"
        />
        {showDropdown && (
          <div className="max-h-48 overflow-y-auto border rounded">
            {filteredTimezones.map((tz) => (
              <div
                key={tz}
                onClick={() => {
                  setTimezone(tz);
                  setSearchTerm("");
                  setShowDropdown(false);
                }}
                className={`p-2 cursor-pointer hover:bg-gray-100 ${
                  tz === timezone ? "bg-blue-50 font-semibold" : ""
                }`}
              >
                {getTimezoneLabel(tz)}
              </div>
            ))}
          </div>
        )}
      </div>

      {getParisEquivalentForDisplay() && (
        <div className="px-4 mb-8">
          <div className="mb-2 text-sm font-medium text-gray-500">
            Reference: Europe/Paris (disabled)
          </div>
          <div className="relative">
            <Slider
              range
              min={0}
              max={24}
              step={1}
              value={[0, 24]}
              disabled
              marks={getSliderMarks()}
              styles={{
                track: { backgroundColor: "transparent", height: "10px", top: "2px" },
                handle: { display: "none" },
                rail: { backgroundColor: "#f3f4f6", height: "10px", top: "2px" },
              }}
            />
            {getParisEquivalentForDisplay().segments.map((segment, idx) => {
              const [start, end] = segment;
              const left = (start / 24) * 100;
              const width = ((end - start) / 24) * 100;
              return (
                <div
                  key={idx}
                  className="absolute"
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    height: "10px",
                    top: "2px",
                    backgroundColor: "#d1d5db",
                    borderRadius: "4px",
                  }}
                />
              );
            })}
          </div>
          <div className="mt-8 text-sm text-gray-500">
            <div className="font-medium mb-1">Sending window (Paris time):</div>
            <div>
              Start: <strong>{formatTimeRange(getParisEquivalentForDisplay().rawStart, getParisEquivalentForDisplay().rawEnd, "Europe/Paris").start}</strong>
              {" "} | End: <strong>{formatTimeRange(getParisEquivalentForDisplay().rawStart, getParisEquivalentForDisplay().rawEnd, "Europe/Paris").end}</strong>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 mb-8">
        <div className="mb-2 text-sm font-medium text-gray-700">
          Delivery hours ({timezone})
        </div>
        <Slider
          range
          min={0}
          max={24}
          step={1}
          value={range}
          onChange={setRange}
          marks={getSliderMarks()}
          styles={{
            track: { backgroundColor: "#22c55e", height: "10px", top: "2px" },
            handle: { borderColor: "#3b82f6" },
            rail: { height: "10px", top: "2px" },
          }}
        />
        <div className="mt-8 text-sm text-gray-700">
          <div className="font-medium mb-1">Sending window ({timezone}):</div>
          <div>
            Start: <strong>{formatTimeRange(range[0], range[1], timezone).start}</strong>
            {" "} | End: <strong>{formatTimeRange(range[0], range[1], timezone).end}</strong>
          </div>
        </div>
      </div>

      {isNightRisk() && (
        <div className="p-3 mb-4 bg-amber-50 border-l-4 border-amber-500 text-amber-700 text-sm">
          ⚠️ <strong>Warning:</strong> Sending messages between 22h-8h may
          impact WhatsApp account quality (high report rate).
        </div>
      )}
    </div>
  );
});

DeliveryHoursSettings.displayName = "DeliveryHoursSettings";

export default DeliveryHoursSettings;
